import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { settingsAPI } from '../../lib/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Phone, Mail, MapPin, MessageCircle, Globe, Facebook, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';

const AdminSettings = () => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    whatsapp_number: '',
    site_title: '',
    site_description: '',
    phone: '',
    email: '',
    address: '',
    facebook_url: '',
    instagram_url: '',
  });

  const { data: settingsRes, isLoading: loading, refetch: loadSettings } = useApiQuery(
    ['settings'],
    () => settingsAPI.get()
  );

  useEffect(() => {
    if (settingsRes?.data) {
      setFormData({
        whatsapp_number: settingsRes.data.whatsapp_number || '',
        site_title: settingsRes.data.site_title || '',
        site_description: settingsRes.data.site_description || '',
        phone: settingsRes.data.phone || '',
        email: settingsRes.data.email || '',
        address: settingsRes.data.address || '',
        facebook_url: settingsRes.data.facebook_url || '',
        instagram_url: settingsRes.data.instagram_url || '',
      });
    }
  }, [settingsRes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsAPI.update(formData);
      toast.success('Settings updated successfully');
      loadSettings();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="h-64 bg-slate-200 rounded-xl skeleton" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Settings
          </h1>
          <p className="text-slate-600">Configure your store settings and contact information</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="settings-form">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#8acb95]" />
                General Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Site Title</label>
                  <Input
                    value={formData.site_title}
                    onChange={(e) => setFormData({ ...formData, site_title: e.target.value })}
                    placeholder="Gulf Star"
                    data-testid="site-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Site Description</label>
                  <Input
                    value={formData.site_description}
                    onChange={(e) => setFormData({ ...formData, site_description: e.target.value })}
                    placeholder="For Wholesale And Retail Trade"
                    data-testid="site-description-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-[#8acb95]" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-green-500" />
                    WhatsApp Number *
                  </label>
                  <Input
                    required
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                    placeholder="9651234567890"
                    data-testid="whatsapp-number-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Enter with country code, without + or spaces</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-[#8acb95]" />
                    Phone Number
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+965 1234 5678"
                    data-testid="phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#8acb95]" />
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="info@gulfstar.com"
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#8acb95]" />
                    Address
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Kuwait City, Kuwait"
                    data-testid="address-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Media */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Instagram className="w-5 h-5 text-[#8acb95]" />
                Social Media Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-600" />
                    Facebook URL
                  </label>
                  <Input
                    value={formData.facebook_url}
                    onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                    placeholder="https://facebook.com/gulfstar"
                    data-testid="facebook-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-pink-600" />
                    Instagram URL
                  </label>
                  <Input
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                    placeholder="https://instagram.com/gulfstar"
                    data-testid="instagram-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button 
            type="submit" 
            disabled={saving}
            className="bg-[#8acb95] text-white hover:bg-[#7bb885]" 
            data-testid="save-settings-button"
          >
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
